import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog, ArchiveConfirmDialog } from './Modal';
import Icon from './Icon';
import { getAccessToken, isSecretaryContextUser } from '../authStorage';
import { decisionStatusLabel } from '../decisionFormUtils';
import { deleteDecision, fetchDecisions } from '../decisionsApi';
import { archiveEntity } from '../archiveApi';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import { useBranchFilter } from '../branchFilter';
import './BranchesPage.css';
import './DecisionsPage.css';

function DecisionsPage() {
  const { language, direction } = useLanguage();
  const t = useT();
  const navigate = useNavigate();
  const { selectedBranch: globalBranch } = useBranchFilter();
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setFilter] = useState('all');
  const [filterBranch, setFilterBranch] = useState('all');
  const [viewMode, setViewMode] = useState('table');
  const [currentPage, setPage] = useState(1);
  const [loadingList, setLoadingList] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [archiveConfirmId, setArchiveConfirmId] = useState(null);
  const [archiving, setArchiving] = useState(false);

  const STATUS_CONFIG = useMemo(() => ({
    pending:  { label: decisionStatusLabel('pending', language),  iconName: 'clock',        cls: 'status-badge--pending' },
    approved: { label: decisionStatusLabel('approved', language), iconName: 'check-circle', cls: 'status-badge--approved' },
    rejected: { label: decisionStatusLabel('rejected', language), iconName: 'x-circle',     cls: 'status-badge--rejected' },
  }), [language]);

  const fetchedRef = useRef(false);
  useEffect(() => {
    if (!getAccessToken()) { navigate('/login', { replace: true }); return; }
    if (!isSecretaryContextUser()) { setAccessDenied(true); setLoadingList(false); return; }
    // Fetch exactly once per mount — prevents StrictMode's double-invoke from
    // firing duplicate /api/decisions requests (the "keeps reloading" issue).
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    (async () => {
      setLoadError(''); setLoadingList(true);
      try {
        const rows = await fetchDecisions();
        setData(rows);
      } catch (e) {
        setLoadError(e instanceof Error ? e.message : t('common.errorLoad'));
      } finally {
        setLoadingList(false);
      }
    })();
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const PAGE_SIZE = viewMode === 'cards' ? 6 : 5;

  // Global navbar branch overrides the local page filter
  const effectiveBranch = globalBranch !== 'all' ? globalBranch : filterBranch;

  const filtered = useMemo(() => data.filter((d) => {
    const matchSearch = !search || d.title.includes(search) || d.branch.includes(search) || d.description.includes(search);
    const matchStatus = statusFilter === 'all' || d.status === statusFilter;
    const matchBranch = effectiveBranch === 'all' || d.branch === effectiveBranch;
    return matchSearch && matchStatus && matchBranch;
  }), [data, search, statusFilter, effectiveBranch]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const counts = useMemo(() => ({
    all:      data.length,
    pending:  data.filter((d) => d.status === 'pending').length,
    approved: data.filter((d) => d.status === 'approved').length,
    rejected: data.filter((d) => d.status === 'rejected').length,
  }), [data]);

  const STATUS_TABS = useMemo(() => [
    { key: 'all',      label: `${t('decisions.tabAll')} (${counts.all})`,                                     iconName: 'list',         activeCls: 'status-tab--active-all' },
    { key: 'pending',  label: `${decisionStatusLabel('pending', language)} (${counts.pending})`,               iconName: 'clock',        activeCls: 'status-tab--active-pending' },
    { key: 'approved', label: `${decisionStatusLabel('approved', language)} (${counts.approved})`,             iconName: 'check-circle', activeCls: 'status-tab--active-approved' },
    { key: 'rejected', label: `${decisionStatusLabel('rejected', language)} (${counts.rejected})`,             iconName: 'x-circle',     activeCls: 'status-tab--active-rejected' },
  ], [counts, language, t]);

  const handleArchive = async () => {
    if (!archiveConfirmId) return;
    setArchiving(true);
    try {
      await archiveEntity('Decision', archiveConfirmId);
      setData((prev) => prev.filter((d) => d.id !== archiveConfirmId));
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : t('archive.errorArchive'));
    } finally {
      setArchiving(false);
      setArchiveConfirmId(null);
    }
  };

  const openDelete = (item) => { setDeleteTarget(item); setConfirmOpen(true); };
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDecision(deleteTarget.id);
      setData((prev) => prev.filter((d) => d.id !== deleteTarget.id));
      setConfirmOpen(false); setDeleteTarget(null); setPage(1);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : t('common.errorDelete'));
      setConfirmOpen(false); setDeleteTarget(null);
    }
  };

  if (accessDenied) return <p style={{ padding: 24, color: '#78909c', fontFamily: 'Cairo, sans-serif' }}>{t('common.noPerm')}</p>;

  return (
    <div className="page" dir={direction}>
      <div className="list-page-card list-page-card--unified">
        <header className="list-page-card__header">
          <div className="list-page-card__titles">
            <span className="list-page-card__title"><Icon name="decisions" size={18} color="#00334a" style={{ verticalAlign: 'middle', marginLeft: 6 }} />{t('decisions.title')}</span>
            <span className="list-page-card__sub">{t('decisions.subtitle')}</span>
          </div>
          <div className="list-page-card__actions">
            <div className="view-toggle">
              <button type="button" className={`view-toggle__btn${viewMode === 'cards' ? ' view-toggle__btn--active' : ''}`} onClick={() => setViewMode('cards')}><Icon name="grid" size={15} /></button>
              <button type="button" className={`view-toggle__btn${viewMode === 'table' ? ' view-toggle__btn--active' : ''}`} onClick={() => setViewMode('table')}><Icon name="list" size={15} /></button>
            </div>
            <button type="button" className="btn btn--ghost" onClick={() => navigate('/app/archive?type=Decision')}><Icon name="archive" size={14} /> {t('nav.archive')}</button>
            <button type="button" className="btn btn--primary" onClick={() => navigate('/app/decisions/new')}><Icon name="plus" size={14} color="#fff" /> {t('decisions.new')}</button>
          </div>
        </header>
        {loadError && <div className="list-page-card__banner list-page-card__banner--error">{loadError}</div>}
        {loadingList && <div className="list-page-card__loading">{t('common.loading')}</div>}
      {!loadingList && (
      <>
      <div className="status-tabs">
        {STATUS_TABS.map((tab) => (
          <button key={tab.key} type="button" className={`status-tab${statusFilter === tab.key ? ` ${tab.activeCls}` : ''}`} onClick={() => { setFilter(tab.key); setPage(1); }}>
            <Icon name={tab.iconName} size={13} style={{ verticalAlign: 'middle', marginLeft: 5 }} />
            {tab.label}
          </button>
        ))}
      </div>

      <div className="page__filters">
        <div className="page__search">
          <Icon name="search" size={14} color="#90a4ae" className="page__search-icon" />
          <input className="page__search-input" placeholder={t('decisions.searchPh')} value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
          {search && <span role="button" tabIndex={0} style={{ cursor: 'pointer', color: '#90a4ae', fontSize: '0.8rem' }} onClick={() => setSearch('')} onKeyDown={(e) => e.key === 'Enter' && setSearch('')}>✕</span>}
        </div>
        <select className="page__filter-select" value={filterBranch} onChange={(e) => { setFilterBranch(e.target.value); setPage(1); }}>
          <option value="all">{t('decisions.allBranches')}</option>
          {Array.from(new Set(data.map((x) => x.branch))).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {viewMode === 'cards' && (
        <>
          {paginated.length === 0 ? (
            <div className="table-card">
              <div className="empty-state">
                <div className="empty-state__icon">📄</div>
                <div className="empty-state__title">{t('decisions.noMatch')}</div>
                <div className="empty-state__sub">{t('decisions.noMatchSub')}</div>
                <button type="button" className="btn btn--primary" onClick={() => navigate('/app/decisions/new')}>＋ {t('decisions.addDecision')}</button>
              </div>
            </div>
          ) : (
            <div className="decisions-grid">
              {paginated.map((item, i) => {
                const s = STATUS_CONFIG[item.status] || STATUS_CONFIG.pending;
                return (
                  <div key={item.id} className={`decision-card decision-card--${item.status}`} style={{ animationDelay: `${i * 0.06}s` }}>
                    <div className="decision-card__top">
                      <div className="decision-card__title">{item.title}</div>
                      <span className={`status-badge ${s.cls}`}><Icon name={s.iconName} size={11} /> {s.label}</span>
                    </div>
                    <div className="decision-card__body">{item.description}</div>
                    <div className="decision-card__meta">
                      <span className="decision-card__meta-item">🏢 {item.branch}</span>
                      <span className="decision-card__meta-item">👤 {item.creator}</span>
                      <span className="decision-card__meta-item">📅 {item.date}</span>
                    </div>
                    <div className="decision-card__footer">
                      <div className="td-actions">
                        <button type="button" className="action-btn action-btn--view" title={t('decisions.actionView')} onClick={() => navigate(`/app/decisions/${item.id}`)}><Icon name="eye" size={14} /></button>
                        <button type="button" className="action-btn action-btn--edit" title={t('decisions.actionEdit')} onClick={() => navigate(`/app/decisions/${item.id}/edit`)}><Icon name="edit" size={14} /></button>
                        <button type="button" className="action-btn action-btn--archive" title={t('archive.title')} onClick={() => setArchiveConfirmId(item.id)}><Icon name="archive" size={14} /></button>
                        <button type="button" className="action-btn action-btn--delete" title={t('decisions.actionDelete')} onClick={() => openDelete(item)}><Icon name="trash" size={14} /></button>
                      </div>
                      <span style={{ fontSize: '0.72rem', color: '#90a4ae' }}>#{item.id}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {viewMode === 'table' && (
        <div className="table-card">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 42 }}>#</th>
                <th>{t('decisions.colTitle')}</th>
                <th>{t('decisions.colBranch')}</th>
                <th>{t('decisions.colCreator')}</th>
                <th>{t('decisions.colDate')}</th>
                <th>{t('decisions.colStatus')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr><td colSpan={7}>
                  <div className="empty-state">
                    <div className="empty-state__icon">📄</div>
                    <div className="empty-state__title">{t('decisions.noMatch')}</div>
                    <div className="empty-state__sub">{t('decisions.noMatchSub')}</div>
                    <button type="button" className="btn btn--primary" onClick={() => navigate('/app/decisions/new')}>＋ {t('decisions.addDecision')}</button>
                  </div>
                </td></tr>
              ) : (
                paginated.map((item, idx) => {
                  const s = STATUS_CONFIG[item.status] || STATUS_CONFIG.pending;
                  return (
                    <tr key={item.id}>
                      <td className="td-num">{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                      <td><strong style={{ color: '#00334a' }}>{item.title}</strong></td>
                      <td>{item.branch}</td>
                      <td>{item.creator}</td>
                      <td>{item.date}</td>
                      <td><span className={`status-badge ${s.cls}`}><Icon name={s.iconName} size={11} /> {s.label}</span></td>
                      <td>
                        <div className="td-actions">
                          <button type="button" className="action-btn action-btn--view" title={t('common.view')} onClick={() => navigate(`/app/decisions/${item.id}`)}><Icon name="eye" size={14} /></button>
                          <button type="button" className="action-btn action-btn--edit" title={t('common.edit')} onClick={() => navigate(`/app/decisions/${item.id}/edit`)}><Icon name="edit" size={14} /></button>
                          <button type="button" className="action-btn action-btn--archive" title={t('archive.title')} onClick={() => setArchiveConfirmId(item.id)}><Icon name="archive" size={14} /></button>
                          <button type="button" className="action-btn action-btn--delete" title={t('common.delete')} onClick={() => openDelete(item)}><Icon name="trash" size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          {filtered.length > PAGE_SIZE && (
            <div className="pagination">
              <span>{t('common.showing')} {Math.min(filtered.length, (currentPage - 1) * PAGE_SIZE + 1)}–{Math.min(filtered.length, currentPage * PAGE_SIZE)} {t('common.of')} {filtered.length}</span>
              <div className="pagination__pages">
                <button type="button" className="pagination__btn" onClick={() => setPage((p) => p - 1)} disabled={currentPage === 1}>‹</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button key={p} type="button" className={`pagination__btn${currentPage === p ? ' pagination__btn--active' : ''}`} onClick={() => setPage(p)}>{p}</button>
                ))}
                <button type="button" className="pagination__btn" onClick={() => setPage((p) => p + 1)} disabled={currentPage === totalPages}>›</button>
              </div>
            </div>
          )}
        </div>
      )}

      {viewMode === 'cards' && filtered.length > PAGE_SIZE && (
        <div className="table-card" style={{ marginTop: 0, borderRadius: '0 0 16px 16px', borderTop: 'none' }}>
          <div className="pagination">
            <span>{t('common.showing')} {Math.min(filtered.length, (currentPage - 1) * PAGE_SIZE + 1)}–{Math.min(filtered.length, currentPage * PAGE_SIZE)} {t('common.of')} {filtered.length}</span>
            <div className="pagination__pages">
              <button type="button" className="pagination__btn" onClick={() => setPage((p) => p - 1)} disabled={currentPage === 1}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button key={p} type="button" className={`pagination__btn${currentPage === p ? ' pagination__btn--active' : ''}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button type="button" className="pagination__btn" onClick={() => setPage((p) => p + 1)} disabled={currentPage === totalPages}>›</button>
            </div>
          </div>
        </div>
      )}
      </>
      )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        itemName={deleteTarget?.title}
        title={t('decisions.deleteTitle')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
      />

      <ArchiveConfirmDialog
        open={!!archiveConfirmId}
        onClose={() => setArchiveConfirmId(null)}
        onConfirm={handleArchive}
        itemName={data.find((d) => d.id === archiveConfirmId)?.title}
        title={t('archive.restoreConfirm').replace('?', '') || t('archive.title')}
        confirmLabel={t('archive.title')}
        cancelLabel={t('common.cancel')}
        loading={archiving}
      />
    </div>
  );
}

export default DecisionsPage;
